/**
 * @description Component-TradePortfolioTabs
 */
import { useCurrentPairFromUrl } from '@/features/pair/hook';
import './index.less';

import SynTitleTabs from '@/components/Tabs/SynTitleTabs';
import { useFilteredLiquidities } from '@/hooks/trade/useFilteredLiquidities';
import { useFilteredOrders } from '@/hooks/trade/useFilteredOrders';
import { useFilteredPositions } from '@/hooks/trade/useFilteredPositions';
import { useChainId } from '@/hooks/web3/useChain';
import classNames from 'classnames';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export enum TabKeys {
  POSITIONS = 'positions',
  OPEN_ORDERS = 'openOrders',
  LIQUIDITY = 'liquidity',
  HISTORY = 'history',
  ACCOUNT = 'account',
}

interface ITradePortfolioTabsProps {
  activeTab: TabKeys;
  onTabClick: (key: string) => void;
  className?: string;
}

const TradePortfolioTabs: FC<ITradePortfolioTabsProps> = function ({ activeTab, onTabClick, className }) {
  const { t } = useTranslation();
  const chainId = useChainId();
  const currentPair = useCurrentPairFromUrl(chainId);
  const orders = useFilteredOrders(currentPair);
  const positionList = useFilteredPositions({ currentPair });
  const liquidities = useFilteredLiquidities(currentPair);
  const tabItems = useMemo(
    () => [
      {
        key: TabKeys.POSITIONS,
        label: (
          <>
            {t('common.position')}
            {(positionList?.length || 0) > 0 && <span className="syn-title-tabs-count">({positionList?.length})</span>}
          </>
        ),
      },
      {
        key: TabKeys.OPEN_ORDERS,
        label: (
          <>
            {t('common.openOrders')}
            {orders?.length > 0 && <span className="syn-title-tabs-count">({orders?.length})</span>}
          </>
        ),
      },
      {
        key: TabKeys.LIQUIDITY,
        label: (
          <>
            {t('common.liquidity')}
            {liquidities?.length > 0 && <span className="syn-title-tabs-count">({liquidities?.length})</span>}
          </>
        ),
      },
      {
        key: TabKeys.HISTORY,
        label: t('common.history'),
      },
    ],
    [t, positionList?.length, orders?.length, liquidities?.length],
  );

  return (
    <div className={classNames('syn-trade-portfolio-tabs', className)}>
      <SynTitleTabs items={tabItems} activeKey={activeTab} onClick={onTabClick} />
    </div>
  );
};

export default TradePortfolioTabs;
